import { GraphQLClient, RequestOptions } from "graphql-request"
import * as Operations from "./bounceSdk.gen"
import { useQuery, useMutation, UseQueryOptions, UseMutationOptions } from "@tanstack/react-query"
import { bounceClient } from "./bounceClient.client"
type GraphQLClientRequestHeaders = RequestOptions["requestHeaders"]
import * as Types from "./bounceSdk.types.gen"
export type QueryError = Error | import("graphql-request").ClientError

export type SdkFunctionWrapper = <T>(
  action: (requestHeaders?: Record<string, string>) => Promise<T>,
  operationName: string,
  operationType?: string,
  variables?: any,
) => Promise<T>

const defaultWrapper: SdkFunctionWrapper = (action, _operationName, _operationType, _variables) => action()

export function getSdk(client: GraphQLClient, withWrapper: SdkFunctionWrapper = defaultWrapper) {
  return {
    GetCity(
      variables: Types.GetCityQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetCityQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetCityQuery>({
            document: Operations.GetCityDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetCity",
        "query",
        variables,
      )
    },
    GetCities(
      variables: Types.GetCitiesQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetCitiesQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetCitiesQuery>({
            document: Operations.GetCitiesDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetCities",
        "query",
        variables,
      )
    },
    GetNearbyCities(
      variables: Types.GetNearbyCitiesQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetNearbyCitiesQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetNearbyCitiesQuery>({
            document: Operations.GetNearbyCitiesDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetNearbyCities",
        "query",
        variables,
      )
    },
    GetLuggageStorageSitemapCities(
      variables: Types.GetLuggageStorageSitemapCitiesQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetLuggageStorageSitemapCitiesQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetLuggageStorageSitemapCitiesQuery>({
            document: Operations.GetLuggageStorageSitemapCitiesDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetLuggageStorageSitemapCities",
        "query",
        variables,
      )
    },
    GetCountries(
      variables?: Types.GetCountriesQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetCountriesQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetCountriesQuery>({
            document: Operations.GetCountriesDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetCountries",
        "query",
        variables,
      )
    },
    Experiments(
      variables?: Types.ExperimentsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.ExperimentsQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.ExperimentsQuery>({
            document: Operations.ExperimentsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "Experiments",
        "query",
        variables,
      )
    },
    RegisterUser(
      variables: Types.RegisterUserMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.RegisterUserMutation> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.RegisterUserMutation>({
            document: Operations.RegisterUserDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "RegisterUser",
        "mutation",
        variables,
      )
    },
    SubscribeNewsletter(
      variables: Types.SubscribeNewsletterMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.SubscribeNewsletterMutation> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.SubscribeNewsletterMutation>({
            document: Operations.SubscribeNewsletterDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "SubscribeNewsletter",
        "mutation",
        variables,
      )
    },
    GetPoi(
      variables: Types.GetPoiQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetPoiQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetPoiQuery>({
            document: Operations.GetPoiDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetPoi",
        "query",
        variables,
      )
    },
    GetNearbyPois(
      variables: Types.GetNearbyPoisQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetNearbyPoisQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetNearbyPoisQuery>({
            document: Operations.GetNearbyPoisDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetNearbyPois",
        "query",
        variables,
      )
    },
    RequestAPPBySMS(
      variables: Types.RequestAppBySmsMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.RequestAppBySmsMutation> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.RequestAppBySmsMutation>({
            document: Operations.RequestAppBySmsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "RequestAPPBySMS",
        "mutation",
        variables,
      )
    },
    AggregateRating(
      variables?: Types.AggregateRatingQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.AggregateRatingQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.AggregateRatingQuery>({
            document: Operations.AggregateRatingDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "AggregateRating",
        "query",
        variables,
      )
    },
    Reviews(
      variables: Types.ReviewsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.ReviewsQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.ReviewsQuery>({
            document: Operations.ReviewsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "Reviews",
        "query",
        variables,
      )
    },
    GetStoreLocation(
      variables: Types.GetStoreLocationQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetStoreLocationQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetStoreLocationQuery>({
            document: Operations.GetStoreLocationDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetStoreLocation",
        "query",
        variables,
      )
    },
    GetNearbyStorageSpots(
      variables: Types.GetNearbyStorageSpotsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetNearbyStorageSpotsQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetNearbyStorageSpotsQuery>({
            document: Operations.GetNearbyStorageSpotsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetNearbyStorageSpots",
        "query",
        variables,
      )
    },
    GetStoresSitemap(
      variables: Types.GetStoresSitemapQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetStoresSitemapQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetStoresSitemapQuery>({
            document: Operations.GetStoresSitemapDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetStoresSitemap",
        "query",
        variables,
      )
    },
    GetTags(
      variables?: Types.GetTagsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetTagsQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetTagsQuery>({
            document: Operations.GetTagsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetTags",
        "query",
        variables,
      )
    },
    GetServices(
      variables?: Types.GetServicesQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetServicesQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetServicesQuery>({
            document: Operations.GetServicesDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetServices",
        "query",
        variables,
      )
    },
    GetCitiesForSitemap(
      variables?: Types.GetCitiesForSitemapQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.GetCitiesForSitemapQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.GetCitiesForSitemapQuery>({
            document: Operations.GetCitiesForSitemapDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetCitiesForSitemap",
        "query",
        variables,
      )
    },
    NearestCity(
      variables: Types.NearestCityQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<Types.NearestCityQuery> {
      return withWrapper(
        wrappedRequestHeaders =>
          client.request<Types.NearestCityQuery>({
            document: Operations.NearestCityDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "NearestCity",
        "query",
        variables,
      )
    },
  }
}
export type Sdk = ReturnType<typeof getSdk>

export const CityTranslationsFragmentDoc = `
    fragment CityTranslations on City {
  translations {
    name(locale: $locale)
  }
}
    `
export const PoiTranslationsFragmentDoc = `
    fragment PoiTranslations on Poi {
  translations {
    name(locale: $locale)
  }
}
    `
export const CityFragmentDoc = `
    fragment City on City {
  id
  slug
  name
  active
  avgMonthlySearchesQty
  paFeatured
  featured
  query
  country
  region
  luggagePrice
  lowestPrice
  prismicId
  currency
  timezone
  labels {
    name
    metadata
  }
  marketingLanguages
  coordinates {
    latitude
    longitude
  }
  nearestStore @include(if: $withNearest) {
    distance
  }
  pois @include(if: $withPois) {
    ...PoiTranslations
    id
    slug
    directorySlug
    name
    query
    avgMonthlySearchesQty
    active
    marketingLanguages
    coordinates {
      latitude
      longitude
    }
  }
  directorySlug
}
    ${PoiTranslationsFragmentDoc}`
export const ExperimentFragmentDoc = `
    fragment Experiment on Experiment {
  id
  name
  variants {
    key
    value
    payload
  }
  key
}
    `
export const LoginResponseFragmentDoc = `
    fragment LoginResponse on LoginResponse {
  accessToken
  refreshToken
  expiry
}
    `
export const ReviewFragmentDoc = `
    fragment Review on Review {
  feedback
  rating
  id
  user {
    firstName
    lastName
    profilePictureUrl
  }
  completedAt
  store {
    id
    city {
      id
      slug
      name
      translations {
        name(locale: $locale)
      }
    }
  }
  source {
    type
    url
  }
  language
  translatedFeedback @include(if: $withTranslatedFeedback)
}
    `
export const StorageSpotFragmentDoc = `
    fragment StorageSpot on StorageSpot {
  id
  nickname
  type
  reviewCount
  rating
  slug
  lowestPrice
  city {
    currency
    country
    slug
    name
    timezone
  }
  coordinates {
    latitude
    longitude
  }
  marketingPageEnabled
}
    `
export const WithAddressFragmentDoc = `
    fragment WithAddress on StorageSpot {
  address {
    street
    zipCode
  }
}
    `
export const MainStoreFragmentDoc = `
    fragment MainStore on StorageSpot {
  ...WithAddress
  id
  nickname
  gmbPhone
  gmbLocationName
  gmbAddress {
    addressLines
    locality
    administrativeArea
    regionCode
    postalCode
  }
  coordinates {
    latitude
    longitude
  }
}
    ${WithAddressFragmentDoc}`
export const OpeningHourFragmentDoc = `
    fragment OpeningHour on OpeningHour {
  openTime
  closeTime
  dayOfWeek
}
    `
export const WithOpeningHoursFragmentDoc = `
    fragment WithOpeningHours on StorageSpot {
  openingHours {
    ...OpeningHour
  }
}
    ${OpeningHourFragmentDoc}`
export const OpeningHourExceptionFragmentDoc = `
    fragment OpeningHourException on OpeningHourException {
  closeTime
  closed
  startDate
  endDate
  openTime
  reason
}
    `
export const WithOpeningHoursExceptionsFragmentDoc = `
    fragment WithOpeningHoursExceptions on StorageSpot {
  openingHourExceptions {
    ...OpeningHourException
  }
}
    ${OpeningHourExceptionFragmentDoc}`
export const WithStoreCardTertiaryStaticMapFragmentDoc = `
    fragment WithStoreCardTertiaryStaticMap on StorageSpot {
  storeCardTertiaryStaticMapUrl: staticMapUrl(
    zoom: 14
    width: 500
    height: 360
    mapId: "f7bfb7c4215fe617"
    language: $language
    markerStyle: {scale: 1}
  )
}
    `
export const WithGmbFragmentDoc = `
    fragment WithGmb on StorageSpot {
  gmbAddress {
    addressLines
    locality
    administrativeArea
    regionCode
    postalCode
  }
  gmbLocationPlaceId
  gmbLocationName
  gmbPhone
}
    `
export const WithThumbnailFragmentDoc = `
    fragment WithThumbnail on StorageSpot {
  thumbnail {
    externalId
  }
}
    `
export const WithTagsFragmentDoc = `
    fragment WithTags on StorageSpot {
  tags {
    key
  }
}
    `
export const GetCityDocument = `
    query GetCity($slug: String, $coordinates: CoordinatesInput, $locale: String!, $withPois: Boolean!, $withNearest: Boolean! = false, $onlyActive: Boolean! = true) {
  city(slug: $slug, onlyActive: $onlyActive, coordinates: $coordinates) {
    ...City
    ...CityTranslations
    country
    mainStore {
      ...MainStore
      ...WithOpeningHours
    }
    name
    state
    storeCount
  }
}
    ${CityFragmentDoc}
${CityTranslationsFragmentDoc}
${MainStoreFragmentDoc}
${WithOpeningHoursFragmentDoc}`

export const useGetCityQuery = <TData = Types.GetCityQuery, TError = QueryError>(
  variables: Types.GetCityQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetCityQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetCityQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetCityQuery, TError, TData>({
    queryKey: ["GetCity", variables],
    queryFn: bounceClient<Types.GetCityQuery, Types.GetCityQueryVariables>(GetCityDocument, variables),
    ...options,
  })
}

useGetCityQuery.getKey = (variables: Types.GetCityQueryVariables) => ["GetCity", variables]

export const GetCitiesDocument = `
    query GetCities($before: String, $first: Int, $last: Int, $after: String, $orderBy: [CitySortOrder], $locale: String!, $withPois: Boolean!, $withNearest: Boolean! = false, $onlyActive: Boolean! = true) {
  cities(
    before: $before
    first: $first
    last: $last
    after: $after
    orderBy: $orderBy
    onlyActive: $onlyActive
  ) {
    edges {
      node {
        ...City
        ...CityTranslations
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    ${CityFragmentDoc}
${CityTranslationsFragmentDoc}`

export const useGetCitiesQuery = <TData = Types.GetCitiesQuery, TError = QueryError>(
  variables: Types.GetCitiesQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetCitiesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetCitiesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetCitiesQuery, TError, TData>({
    queryKey: ["GetCities", variables],
    queryFn: bounceClient<Types.GetCitiesQuery, Types.GetCitiesQueryVariables>(GetCitiesDocument, variables),
    ...options,
  })
}

useGetCitiesQuery.getKey = (variables: Types.GetCitiesQueryVariables) => ["GetCities", variables]

export const GetNearbyCitiesDocument = `
    query GetNearbyCities($latitude: Float!, $longitude: Float!, $locale: String!, $first: Int = 12) {
  cities(coordinates: {latitude: $latitude, longitude: $longitude}, first: $first) {
    edges {
      node {
        id
        slug
        name
        translations {
          name(locale: $locale)
        }
      }
    }
  }
}
    `

export const useGetNearbyCitiesQuery = <TData = Types.GetNearbyCitiesQuery, TError = QueryError>(
  variables: Types.GetNearbyCitiesQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetNearbyCitiesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetNearbyCitiesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetNearbyCitiesQuery, TError, TData>({
    queryKey: ["GetNearbyCities", variables],
    queryFn: bounceClient<Types.GetNearbyCitiesQuery, Types.GetNearbyCitiesQueryVariables>(
      GetNearbyCitiesDocument,
      variables,
    ),
    ...options,
  })
}

useGetNearbyCitiesQuery.getKey = (variables: Types.GetNearbyCitiesQueryVariables) => ["GetNearbyCities", variables]

export const GetLuggageStorageSitemapCitiesDocument = `
    query GetLuggageStorageSitemapCities($first: Int, $after: String, $locale: String!) {
  cities(first: $first, after: $after, onlyActive: true) {
    edges {
      node {
        id
        slug
        directorySlug
        name
        avgMonthlySearchesQty
        marketingLanguages
        translations {
          name(locale: $locale)
        }
        pois {
          id
          slug
          directorySlug
          name
          avgMonthlySearchesQty
          active
          marketingLanguages
          ...PoiTranslations
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    ${PoiTranslationsFragmentDoc}`

export const useGetLuggageStorageSitemapCitiesQuery = <
  TData = Types.GetLuggageStorageSitemapCitiesQuery,
  TError = QueryError,
>(
  variables: Types.GetLuggageStorageSitemapCitiesQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetLuggageStorageSitemapCitiesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetLuggageStorageSitemapCitiesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetLuggageStorageSitemapCitiesQuery, TError, TData>({
    queryKey: ["GetLuggageStorageSitemapCities", variables],
    queryFn: bounceClient<
      Types.GetLuggageStorageSitemapCitiesQuery,
      Types.GetLuggageStorageSitemapCitiesQueryVariables
    >(GetLuggageStorageSitemapCitiesDocument, variables),
    ...options,
  })
}

useGetLuggageStorageSitemapCitiesQuery.getKey = (variables: Types.GetLuggageStorageSitemapCitiesQueryVariables) => [
  "GetLuggageStorageSitemapCities",
  variables,
]

export const GetCountriesDocument = `
    query GetCountries {
  countries {
    country
    luggagePrice
    lowestPrice
    currency
  }
}
    `

export const useGetCountriesQuery = <TData = Types.GetCountriesQuery, TError = QueryError>(
  variables?: Types.GetCountriesQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetCountriesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetCountriesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetCountriesQuery, TError, TData>({
    queryKey: variables === undefined ? ["GetCountries"] : ["GetCountries", variables],
    queryFn: bounceClient<Types.GetCountriesQuery, Types.GetCountriesQueryVariables>(GetCountriesDocument, variables),
    ...options,
  })
}

useGetCountriesQuery.getKey = (variables?: Types.GetCountriesQueryVariables) =>
  variables === undefined ? ["GetCountries"] : ["GetCountries", variables]

export const ExperimentsDocument = `
    query Experiments {
  experiments {
    ...Experiment
  }
}
    ${ExperimentFragmentDoc}`

export const useExperimentsQuery = <TData = Types.ExperimentsQuery, TError = QueryError>(
  variables?: Types.ExperimentsQueryVariables,
  options?: Omit<UseQueryOptions<Types.ExperimentsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.ExperimentsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ExperimentsQuery, TError, TData>({
    queryKey: variables === undefined ? ["Experiments"] : ["Experiments", variables],
    queryFn: bounceClient<Types.ExperimentsQuery, Types.ExperimentsQueryVariables>(ExperimentsDocument, variables),
    ...options,
  })
}

useExperimentsQuery.getKey = (variables?: Types.ExperimentsQueryVariables) =>
  variables === undefined ? ["Experiments"] : ["Experiments", variables]

export const RegisterUserDocument = `
    mutation RegisterUser($input: RegisterUserInput!) {
  registerUser(input: $input) {
    data {
      ...LoginResponse
    }
  }
}
    ${LoginResponseFragmentDoc}`

export const useRegisterUserMutation = <TError = QueryError, TContext = unknown>(
  options?: UseMutationOptions<Types.RegisterUserMutation, TError, Types.RegisterUserMutationVariables, TContext>,
) => {
  return useMutation<Types.RegisterUserMutation, TError, Types.RegisterUserMutationVariables, TContext>({
    mutationKey: ["RegisterUser"],
    mutationFn: (variables?: Types.RegisterUserMutationVariables) =>
      bounceClient<Types.RegisterUserMutation, Types.RegisterUserMutationVariables>(RegisterUserDocument, variables)(),
    ...options,
  })
}

export const SubscribeNewsletterDocument = `
    mutation SubscribeNewsletter($input: SubscribeNewsletterInput!) {
  subscribeNewsletter(input: $input) {
    status
  }
}
    `

export const useSubscribeNewsletterMutation = <TError = QueryError, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SubscribeNewsletterMutation,
    TError,
    Types.SubscribeNewsletterMutationVariables,
    TContext
  >,
) => {
  return useMutation<Types.SubscribeNewsletterMutation, TError, Types.SubscribeNewsletterMutationVariables, TContext>({
    mutationKey: ["SubscribeNewsletter"],
    mutationFn: (variables?: Types.SubscribeNewsletterMutationVariables) =>
      bounceClient<Types.SubscribeNewsletterMutation, Types.SubscribeNewsletterMutationVariables>(
        SubscribeNewsletterDocument,
        variables,
      )(),
    ...options,
  })
}

export const GetPoiDocument = `
    query GetPoi($slug: String!, $locale: String!, $onlyActive: Boolean! = true, $withCount: Boolean! = true, $distance: Float = 750) {
  poi(slug: $slug, onlyActive: $onlyActive) {
    ...PoiTranslations
    id
    name
    slug
    directorySlug
    query
    avgMonthlySearchesQty
    type
    active
    marketingLanguages
    storeCount(distance: $distance) @include(if: $withCount)
    coordinates {
      latitude
      longitude
    }
    city {
      id
      slug
    }
    mainStore {
      ...MainStore
      ...WithOpeningHours
    }
    labels {
      name
      metadata
    }
  }
}
    ${PoiTranslationsFragmentDoc}
${MainStoreFragmentDoc}
${WithOpeningHoursFragmentDoc}`

export const useGetPoiQuery = <TData = Types.GetPoiQuery, TError = QueryError>(
  variables: Types.GetPoiQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetPoiQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetPoiQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetPoiQuery, TError, TData>({
    queryKey: ["GetPoi", variables],
    queryFn: bounceClient<Types.GetPoiQuery, Types.GetPoiQueryVariables>(GetPoiDocument, variables),
    ...options,
  })
}

useGetPoiQuery.getKey = (variables: Types.GetPoiQueryVariables) => ["GetPoi", variables]

export const GetNearbyPoisDocument = `
    query GetNearbyPois($coordinates: CoordinatesInput, $locale: String!, $first: Int! = 12, $onlyActive: Boolean! = true, $orderBy: [PoiSortOrder]) {
  pois(
    coordinates: $coordinates
    first: $first
    onlyActive: $onlyActive
    orderBy: $orderBy
  ) {
    edges {
      node {
        ...PoiTranslations
        id
        name
        slug
        coordinates {
          latitude
          longitude
        }
        query
        directorySlug
      }
    }
  }
}
    ${PoiTranslationsFragmentDoc}`

export const useGetNearbyPoisQuery = <TData = Types.GetNearbyPoisQuery, TError = QueryError>(
  variables: Types.GetNearbyPoisQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetNearbyPoisQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetNearbyPoisQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetNearbyPoisQuery, TError, TData>({
    queryKey: ["GetNearbyPois", variables],
    queryFn: bounceClient<Types.GetNearbyPoisQuery, Types.GetNearbyPoisQueryVariables>(
      GetNearbyPoisDocument,
      variables,
    ),
    ...options,
  })
}

useGetNearbyPoisQuery.getKey = (variables: Types.GetNearbyPoisQueryVariables) => ["GetNearbyPois", variables]

export const RequestAppBySmsDocument = `
    mutation RequestAPPBySMS($phone: String!) {
  requestAppLinkText(input: {phone: $phone}) {
    status
  }
}
    `

export const useRequestAppBySmsMutation = <TError = QueryError, TContext = unknown>(
  options?: UseMutationOptions<Types.RequestAppBySmsMutation, TError, Types.RequestAppBySmsMutationVariables, TContext>,
) => {
  return useMutation<Types.RequestAppBySmsMutation, TError, Types.RequestAppBySmsMutationVariables, TContext>({
    mutationKey: ["RequestAPPBySMS"],
    mutationFn: (variables?: Types.RequestAppBySmsMutationVariables) =>
      bounceClient<Types.RequestAppBySmsMutation, Types.RequestAppBySmsMutationVariables>(
        RequestAppBySmsDocument,
        variables,
      )(),
    ...options,
  })
}

export const AggregateRatingDocument = `
    query AggregateRating($cityId: String, $storeIds: [String!]) {
  aggregateRating(cityId: $cityId, storeIds: $storeIds) {
    count
    averageRating
  }
}
    `

export const useAggregateRatingQuery = <TData = Types.AggregateRatingQuery, TError = QueryError>(
  variables?: Types.AggregateRatingQueryVariables,
  options?: Omit<UseQueryOptions<Types.AggregateRatingQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AggregateRatingQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AggregateRatingQuery, TError, TData>({
    queryKey: variables === undefined ? ["AggregateRating"] : ["AggregateRating", variables],
    queryFn: bounceClient<Types.AggregateRatingQuery, Types.AggregateRatingQueryVariables>(
      AggregateRatingDocument,
      variables,
    ),
    ...options,
  })
}

useAggregateRatingQuery.getKey = (variables?: Types.AggregateRatingQueryVariables) =>
  variables === undefined ? ["AggregateRating"] : ["AggregateRating", variables]

export const ReviewsDocument = `
    query Reviews($cityId: String, $first: Int!, $locale: String!, $minRating: Int, $orderBy: [ReviewSortOrder], $preferredLanguage: String, $withFeedback: Boolean, $withTranslatedFeedback: Boolean!, $minFeedbackLength: Int, $maxFeedbackLength: Int, $withExternalReviews: Boolean = false) {
  reviews(
    cityId: $cityId
    first: $first
    minRating: $minRating
    orderBy: $orderBy
    preferredLanguage: $preferredLanguage
    withFeedback: $withFeedback
    minFeedbackLength: $minFeedbackLength
    maxFeedbackLength: $maxFeedbackLength
    withExternalReviews: $withExternalReviews
  ) {
    edges {
      node {
        ...Review
      }
    }
  }
}
    ${ReviewFragmentDoc}`

export const useReviewsQuery = <TData = Types.ReviewsQuery, TError = QueryError>(
  variables: Types.ReviewsQueryVariables,
  options?: Omit<UseQueryOptions<Types.ReviewsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.ReviewsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ReviewsQuery, TError, TData>({
    queryKey: ["Reviews", variables],
    queryFn: bounceClient<Types.ReviewsQuery, Types.ReviewsQueryVariables>(ReviewsDocument, variables),
    ...options,
  })
}

useReviewsQuery.getKey = (variables: Types.ReviewsQueryVariables) => ["Reviews", variables]

export const GetStoreLocationDocument = `
    query GetStoreLocation($slug: String, $language: String!, $locale: String!, $staticMapWidth: Int = 600, $staticMapHeight: Int = 558, $firstReviews: Int!, $minReviewsRating: Int!, $minReviewsFeedbackLength: Int!, $maxReviewsFeedbackLength: Int, $reviewsOrderBy: [ReviewSortOrder], $withExternalReviews: Boolean = false) {
  store(slug: $slug) {
    ...StorageSpot
    ...WithAddress
    ...WithGmb
    ...WithOpeningHours
    ...WithOpeningHoursExceptions
    ...WithThumbnail
    staticMapUrl: staticMapUrl(
      zoom: 14
      width: $staticMapWidth
      height: $staticMapHeight
      mapId: "f7bfb7c4215fe617"
      language: $language
      markerStyle: {scale: 1}
    )
    price
    city {
      id
      coordinates {
        latitude
        longitude
      }
      country
      currency
      lowestPrice
      luggagePrice
      name
      query
      slug
      state
      marketingLanguages
      translations {
        name(locale: $locale)
      }
      directorySlug
    }
    marketingPageEnabled
    firstLiveAt
    maxCapacity
    pictures {
      externalId
    }
    reviews(
      first: $firstReviews
      maxFeedbackLength: $maxReviewsFeedbackLength
      minFeedbackLength: $minReviewsFeedbackLength
      minRating: $minReviewsRating
      orderBy: $reviewsOrderBy
      preferredLanguage: $language
      withExternalReviews: $withExternalReviews
      withFeedback: true
    ) {
      edges {
        node {
          completedAt
          feedback
          id
          rating
          translatedFeedback
          language
          user {
            id
            firstName
            lastName
            profilePictureUrl
          }
          source {
            type
            url
          }
        }
      }
    }
  }
}
    ${StorageSpotFragmentDoc}
${WithAddressFragmentDoc}
${WithGmbFragmentDoc}
${WithOpeningHoursFragmentDoc}
${WithOpeningHoursExceptionsFragmentDoc}
${WithThumbnailFragmentDoc}`

export const useGetStoreLocationQuery = <TData = Types.GetStoreLocationQuery, TError = QueryError>(
  variables: Types.GetStoreLocationQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetStoreLocationQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetStoreLocationQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetStoreLocationQuery, TError, TData>({
    queryKey: ["GetStoreLocation", variables],
    queryFn: bounceClient<Types.GetStoreLocationQuery, Types.GetStoreLocationQueryVariables>(
      GetStoreLocationDocument,
      variables,
    ),
    ...options,
  })
}

useGetStoreLocationQuery.getKey = (variables: Types.GetStoreLocationQueryVariables) => ["GetStoreLocation", variables]

export const GetNearbyStorageSpotsDocument = `
    query GetNearbyStorageSpots($after: String, $cityId: String, $citySlug: String, $coordinates: CoordinatesInput, $distance: Float, $first: Int, $locale: String!, $marketingPageEnabled: Boolean, $orderBy: StoreOrderBy = RELEVANCE, $poiSlug: String, $types: [String] = [], $language: String, $country: String, $isOpen247: Boolean, $initialSuggestions: Boolean, $minRating: Float, $withReservationCount: Boolean = false, $firstReviews: Int!, $minReviewsFeedbackLength: Int!, $minReviewsRating: Int!, $preferredLanguage: String = "en", $reviewOrderBy: [ReviewSortOrder], $withExternalReviews: Boolean = true, $withTranslatedFeedback: Boolean = true, $firstPois: Int = 2, $distancePois: Int = 2500) {
  stores(
    cityId: $cityId
    citySlug: $citySlug
    coordinates: $coordinates
    distance: $distance
    first: $first
    marketingPageEnabled: $marketingPageEnabled
    orderBy: $orderBy
    poiSlug: $poiSlug
    types: $types
    country: $country
    isOpen247: $isOpen247
    after: $after
    initialSuggestions: $initialSuggestions
    minRating: $minRating
  ) {
    edges {
      node {
        ...StorageSpot
        ...WithAddress
        ...WithGmb
        ...WithTags
        ...WithThumbnail
        ...WithStoreCardTertiaryStaticMap
        ...WithOpeningHours
        ...WithOpeningHoursExceptions
        city {
          country
          currency
          lowestPrice
          luggagePrice
          name
          state
          timezone
          translations {
            name(locale: $locale)
          }
        }
        firstLiveAt
        maxCapacity
        pois(distance: $distancePois, first: $firstPois) {
          id
          name
          distance
          avgMonthlySearchesQty
          coordinates {
            latitude
            longitude
          }
          translations {
            name(locale: $locale)
          }
        }
        rating
        last7dReservationCount: reservationCount(days: 7) @include(if: $withReservationCount)
        last30dReservationCount: reservationCount(days: 30) @include(if: $withReservationCount)
        last365dReservationCount: reservationCount(days: 365) @include(if: $withReservationCount)
        totalReservationCount: reservationCount(total: true) @include(if: $withReservationCount)
        reviews(
          first: $firstReviews
          minRating: $minReviewsRating
          minFeedbackLength: $minReviewsFeedbackLength
          orderBy: $reviewOrderBy
          preferredLanguage: $preferredLanguage
          withExternalReviews: $withExternalReviews
        ) {
          edges {
            node {
              feedback
              rating
              id
              user {
                firstName
                lastName
                profilePictureUrl
              }
              completedAt
              source {
                type
                url
              }
              language
              translatedFeedback @include(if: $withTranslatedFeedback)
            }
          }
        }
        reviewSummary {
          reasonCounts {
            count
            reason
          }
        }
        type
      }
    }
    pageInfo {
      endCursor
      hasNextPage
    }
  }
}
    ${StorageSpotFragmentDoc}
${WithAddressFragmentDoc}
${WithGmbFragmentDoc}
${WithTagsFragmentDoc}
${WithThumbnailFragmentDoc}
${WithStoreCardTertiaryStaticMapFragmentDoc}
${WithOpeningHoursFragmentDoc}
${WithOpeningHoursExceptionsFragmentDoc}`

export const useGetNearbyStorageSpotsQuery = <TData = Types.GetNearbyStorageSpotsQuery, TError = QueryError>(
  variables: Types.GetNearbyStorageSpotsQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetNearbyStorageSpotsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetNearbyStorageSpotsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetNearbyStorageSpotsQuery, TError, TData>({
    queryKey: ["GetNearbyStorageSpots", variables],
    queryFn: bounceClient<Types.GetNearbyStorageSpotsQuery, Types.GetNearbyStorageSpotsQueryVariables>(
      GetNearbyStorageSpotsDocument,
      variables,
    ),
    ...options,
  })
}

useGetNearbyStorageSpotsQuery.getKey = (variables: Types.GetNearbyStorageSpotsQueryVariables) => [
  "GetNearbyStorageSpots",
  variables,
]

export const GetStoresSitemapDocument = `
    query GetStoresSitemap($first: Int!, $after: String, $marketingPageEnabled: Boolean = true) {
  stores(
    marketingPageEnabled: $marketingPageEnabled
    first: $first
    after: $after
  ) {
    edges {
      node {
        gmbLocationName
        slug
        city {
          slug
          marketingLanguages
        }
      }
    }
    pageInfo {
      endCursor
      hasNextPage
      hasPreviousPage
      startCursor
    }
  }
}
    `

export const useGetStoresSitemapQuery = <TData = Types.GetStoresSitemapQuery, TError = QueryError>(
  variables: Types.GetStoresSitemapQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetStoresSitemapQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetStoresSitemapQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetStoresSitemapQuery, TError, TData>({
    queryKey: ["GetStoresSitemap", variables],
    queryFn: bounceClient<Types.GetStoresSitemapQuery, Types.GetStoresSitemapQueryVariables>(
      GetStoresSitemapDocument,
      variables,
    ),
    ...options,
  })
}

useGetStoresSitemapQuery.getKey = (variables: Types.GetStoresSitemapQueryVariables) => ["GetStoresSitemap", variables]

export const GetTagsDocument = `
    query GetTags($first: Int) {
  tags(first: $first) {
    edges {
      node {
        key
        name
      }
    }
  }
}
    `

export const useGetTagsQuery = <TData = Types.GetTagsQuery, TError = QueryError>(
  variables?: Types.GetTagsQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetTagsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetTagsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetTagsQuery, TError, TData>({
    queryKey: variables === undefined ? ["GetTags"] : ["GetTags", variables],
    queryFn: bounceClient<Types.GetTagsQuery, Types.GetTagsQueryVariables>(GetTagsDocument, variables),
    ...options,
  })
}

useGetTagsQuery.getKey = (variables?: Types.GetTagsQueryVariables) =>
  variables === undefined ? ["GetTags"] : ["GetTags", variables]

export const GetServicesDocument = `
    query GetServices($first: Int) {
  services(first: $first) {
    edges {
      node {
        key
        name
      }
    }
  }
}
    `

export const useGetServicesQuery = <TData = Types.GetServicesQuery, TError = QueryError>(
  variables?: Types.GetServicesQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetServicesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetServicesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetServicesQuery, TError, TData>({
    queryKey: variables === undefined ? ["GetServices"] : ["GetServices", variables],
    queryFn: bounceClient<Types.GetServicesQuery, Types.GetServicesQueryVariables>(GetServicesDocument, variables),
    ...options,
  })
}

useGetServicesQuery.getKey = (variables?: Types.GetServicesQueryVariables) =>
  variables === undefined ? ["GetServices"] : ["GetServices", variables]

export const GetCitiesForSitemapDocument = `
    query GetCitiesForSitemap($first: Int, $after: String) {
  cities(first: $first, after: $after, onlyActive: true) {
    edges {
      node {
        name
        slug
        directorySlug
        marketingLanguages
        pois {
          slug
          directorySlug
          marketingLanguages
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `

export const useGetCitiesForSitemapQuery = <TData = Types.GetCitiesForSitemapQuery, TError = QueryError>(
  variables?: Types.GetCitiesForSitemapQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetCitiesForSitemapQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetCitiesForSitemapQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetCitiesForSitemapQuery, TError, TData>({
    queryKey: variables === undefined ? ["GetCitiesForSitemap"] : ["GetCitiesForSitemap", variables],
    queryFn: bounceClient<Types.GetCitiesForSitemapQuery, Types.GetCitiesForSitemapQueryVariables>(
      GetCitiesForSitemapDocument,
      variables,
    ),
    ...options,
  })
}

useGetCitiesForSitemapQuery.getKey = (variables?: Types.GetCitiesForSitemapQueryVariables) =>
  variables === undefined ? ["GetCitiesForSitemap"] : ["GetCitiesForSitemap", variables]

export const NearestCityDocument = `
    query NearestCity($coordinates: CoordinatesInput!) {
  city(coordinates: $coordinates, onlyActive: true) {
    id
  }
}
    `

export const useNearestCityQuery = <TData = Types.NearestCityQuery, TError = QueryError>(
  variables: Types.NearestCityQueryVariables,
  options?: Omit<UseQueryOptions<Types.NearestCityQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.NearestCityQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.NearestCityQuery, TError, TData>({
    queryKey: ["NearestCity", variables],
    queryFn: bounceClient<Types.NearestCityQuery, Types.NearestCityQueryVariables>(NearestCityDocument, variables),
    ...options,
  })
}

useNearestCityQuery.getKey = (variables: Types.NearestCityQueryVariables) => ["NearestCity", variables]
